<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-row>
            <v-col cols="3">
              <v-autocomplete density="compact" variant="outlined" hide-details="auto" v-model="searchCondition.siteCode"
                :items="siteCodeList" item-title="code_name" item-value="code"
                :label="$t('tanaoroshiAdjustmentHistory.label.siteCode')"
                @update:modelValue="searchCondition.zoneCode = null" :rules="requiredValidation" return-object />
            </v-col>
            <v-col cols="3">
              <v-select density="compact" variant="outlined" hide-details="auto" v-model="searchCondition.zoneCode"
                :items="zoneItem" item-title="name" item-value="code"
                :label="$t('tanaoroshiAdjustmentHistory.label.zoneCode')" :clearable="true" />
            </v-col>
            <v-col cols="3">
              <v-text-field density="compact" variant="outlined" hide-details="auto" v-model="searchCondition.janCode"
                :label="$t('tanaoroshiAdjustmentHistory.label.janCode')" />
            </v-col>
            <v-col cols="3">
              <v-text-field density="compact" variant="outlined" hide-details="auto" v-model="searchCondition.brand"
                :label="$t('tanaoroshiAdjustmentHistory.label.brand')" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="3">
              <v-text-field density="compact" variant="outlined" hide-details="auto"
                v-model="searchCondition.productNumber"
                :label="$t('tanaoroshiAdjustmentHistory.label.productNumber')"
                @input="searchCondition.productNumber = searchCondition.productNumber.toUpperCase()" />
            </v-col>
            <v-col cols="3">
              <DatePicker ref="saveDateFrom" v-model="searchCondition.saveDateFrom"
                :label="$t('tanaoroshiAdjustmentHistory.label.saveDateFrom')"></DatePicker>
            </v-col>
            <v-col cols="3">
              <DatePicker ref="saveDateTo" v-model="searchCondition.saveDateTo"
                :label="$t('tanaoroshiAdjustmentHistory.label.saveDateTo')"></DatePicker>
            </v-col>
            <v-col cols="3" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn theme="dark" class="ml-4 px-8 bg-grey-darken-3" elevation="2" @click="search"
                :loading="isSearching">{{ $t('common.action.search') }}</v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th v-for="header in headers" :key="header" @click.stop="tableSort(header)"
                      :style="cursorCheck(header)">{{ $t("tanaoroshiAdjustmentHistory.label.table." + header) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in filteredDataList" :key="data.no">
                    <template v-for="header in headers" :key="header">
                      <td>{{ data[header] }}</td>
                    </template>
                  </tr>
                  <tr>
                    <td :colspan="headers.length">
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="currentPageNo" :length="maxPageNo" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select density="compact" variant="outlined" hide-details="auto" v-model="displayPerPage"
                    :items="displayPerPageItem" item-title="name" item-value="value"
                    :label="$t('common.label.displayCountPerPage')" @update:modelValue="resetCondition()"
                    return-object />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayContentNo.minNo }} - {{ displayContentNo.maxNo }}{{ $t('common.label.displayedDataRange') }}
                  / {{ dataList.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="9" class="text-left">
              <v-btn dark class="px-8 bg-indigo-darken-1" elevation="2" @click.prevent="moveToTanaoroshiSkuListPage">
                {{ $t('tanaoroshiAdjustmentHistory.action.skuList') }}
              </v-btn>
            </v-col>
            <v-col cols="3" class="text-right">
              <v-btn dark class="ml-4 px-8 bg-indigo-darken-1" elevation="2" @click.prevent="downloadCsv"
                :disabled="dataList.length == 0">{{ $t('common.action.outputCsv') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>
        <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="closeZeroDataDialog" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import DatePicker from "@/components/DatePicker";
import api from "@/apis/staff";
import utils from "@/utils";
import ZeroData from "@/components/dialog/ZeroDataDialog";
import store from "@/store";
import router from "@/router/index";
import Paths from "@/router/indexPaths";

export default {
  components: { AppWrapperView, DatePicker, ZeroData },
  props: {},

  computed: {

    // ページング情報の返却
    displayContentNo() {
      const minNo = this.dataList.length != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * (this.currentPageNo);
      if (maxNo > this.dataList.length || this.displayPerPage.value == 0) {
        return { minNo: minNo, maxNo: this.dataList.length };
      }
      return { minNo: minNo, maxNo: maxNo };
    },

    // 最大ページ
    maxPageNo() {
      if (this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.dataList.length / this.displayPerPage.value);
    },

    // ソート
    sortedTable() {
      let tempArray = this.dataList.map(x => x);
      if (this.sortState.target != "") {
        tempArray = tempArray.sort(this.defaultSorter(this.sortState.ascent));
      }
      tempArray = tempArray.map((x) => {
        x.startedDateTime = utils.putSplitWordToCalendarWithTime(x.startedDateTime);
        x.endedDateTime = utils.putSplitWordToCalendarWithTime(x.endedDateTime);
        x.savedDateTime = utils.putSplitWordToCalendarWithTime(x.savedDateTime);
        return x;
      });
      return tempArray;
    },
    filteredDataList() {
      const tempArray = this.sortedTable;
      return tempArray.slice(this.displayContentNo.minNo - 1, this.displayContentNo.maxNo);
    },
    //ゾーンリスト
    zoneItem() {
      const selectedSite = this.siteCodeList.find(x => x.code == this.searchCondition?.siteCode.code);
      return selectedSite?.zoneList || [];
    },
    // ヘッダー
    headers() {
      return this.$getTableDef("TanaoroshiAdjustmentHistoryPage");
    },
  },

  data: () => ({
    currentPageNo: 1,
    prevRoutePath: "",
    // 1ページ表示数
    displayPerPage: {},
    displayPerPageItem: [],
    // ソートの状態
    sortState: { target: "", ascent: true },
    siteCodeList: [],
    dataList: [],

    // 検索条件
    searchCondition: {
      siteCode: "",
      zoneCode: null,
      janCode: "",
      brand: "",
      productNumber: "",
      saveDateFrom: "",
      saveDateTo: "",
    },
    // 0件取得時の警告ダイアログ
    zeroDataWarningDialogFlg: false,
    // 検索中
    isSearching: false,
  }),


  methods: {
    // 汎用的なソート関数を返却する
    defaultSorter(isAscent) {
      return (a, b) => {
        const multiplier = isAscent ? 1 : -1;
        if (a[this.sortState.target] > b[this.sortState.target]) return 1 * multiplier;
        if (a[this.sortState.target] < b[this.sortState.target]) return -1 * multiplier;
        return 0;
      }
    },
    // カーソル表示形式を変更
    cursorCheck() {
      return "cursor: pointer;"
    },

    // 店舗検索リスト取得
    getValidShopSearchList() {
      // selectSiteFlag：0：他店舗含む / 1：自店舗のみ
      api.post("/store/search", { selectSiteFlag: 1 })
        .then((response) => {
          if (response.status == 1) return;
          response.data.results?.shopList.forEach(element => {
            this.siteCodeList.push({
              name: element.name,
              code: element.code,
              zoneList: element.zoneList,
              code_name: element.code + '_' + element.name
            })
          })
          this.searchCondition.siteCode = this.siteCodeList[0];
        })
        .catch((error) => {
          //do nothing
        })
    },

    // テーブルの条件リセット
    resetCondition() {
      this.currentPageNo = 1;
      this.sortState.target = "";
      this.sortState.ascent = true;
    },
    tableSort(headValue) {
      if (headValue == "no") return;
      this.currentPageNo = 1;
      if (this.sortState.target == headValue) {
        this.sortState.ascent = !this.sortState.ascent;
      } else {
        this.sortState.target = headValue;
        this.sortState.ascent = true;
      }
    },

    clear() {
      this.$refs.saveDateFrom.clear()
      this.$refs.saveDateTo.clear()
      this.searchCondition = {
        siteCode: this.siteCodeList[0],
        zoneCode: "",
        janCode: "",
        brand: "",
        movingDateFrom: "",
        productNumber: "",
        saveDateFrom: "",
        saveDateTo: ""
      }
    },

    closeZeroDataDialog() {
      this.zeroDataWarningDialogFlg = false;
    },

    search() {
      this.isSearching = true;
      api.post("/tanaoroshi/adjustment/search-history", {
        siteCode: this.searchCondition.siteCode?.code ?? "",
        zoneCode: this.searchCondition.zoneCode ?? "",
        janCode: this.searchCondition.janCode,
        productNumber: this.searchCondition.productNumber.toUpperCase(),
        saveDateFrom: this.searchCondition.saveDateFrom?.replaceAll("-", "") ?? "",
        saveDateTo: this.searchCondition.saveDateTo?.replaceAll("-", "") ?? "",
        brand: this.searchCondition.brand,
      }).then((response) => {
        this.dataList = response.data.results?.historyList ?? [];
        this.resetCondition();
        // 0件ダイアログ処理
        if (this.dataList.length == 0) this.zeroDataWarningDialogFlg = true;
        this.isSearching = false;
      }).catch((error) => {
        this.dataList = [];
        this.zeroDataWarningDialogFlg = true;
        this.isSearching = false;
      });
    },
    // CSVダウンロード処理
    downloadCsv() {
      utils.simpleDownloadCsv(this.headers, this.sortedTable, "tanaoroshiAdjustmentHistory.label.table.", 'tanaoroshiAdjustmentHistory.fileNamePrefix.csv', this.$t);
    },
    moveToTanaoroshiProductCodePage() {
      this.moveToTanaoroshiPage(Paths.TANAOROSHI_PRODUCTCODE_LIST);
    },
    moveToTanaoroshiSkuListPage() {
      this.moveToTanaoroshiPage(Paths.TANAOROSHI_SKU_LIST);
    },
    moveToTanaoroshiPage(destinationScreenPath) {
      const siteCode = this.searchCondition.siteCode?.code ?? "";
      if (siteCode == "") return;

      this.isSearching = true;

      // 最新の棚卸情報を取得し、棚卸実績一覧に遷移する
      api.post("/tanaoroshi/search-by-site", {
        siteCodeList: [siteCode],
      })
        .then((response) => {
          const inventoryInfoList = response.data.results?.tanaoroshiSearchSiteList ?? [];
          const notFoundErr = new Error("棚卸が存在しない");
          if (inventoryInfoList.length == 0) throw notFoundErr;

          const inventoryInfo = inventoryInfoList[0];
          if (inventoryInfo.inventoryNo == null)  throw notFoundErr;
          const tanaoroshiData = {
            siteCode: inventoryInfo.siteCode,
            siteName: inventoryInfo.siteName,
            inventoryNo: inventoryInfo.inventoryNo
          };
          store.setTanaoroshi(tanaoroshiData);
          router.push({ name: destinationScreenPath })
        })
        .catch((error) => {
          this.zeroDataWarningDialogFlg = true;
        })
        .finally(() => {
          this.isSearching = false;
        })
    },
  },
  created() {
    this.getValidShopSearchList()
  },
  mounted() {
    this.displayPerPage = utils.getDisplayPerPage(this.$t)
    this.displayPerPageItem = utils.getDisplayPerPageItem(this.$t);
  }
};
</script>
